// src/components/Warehouses/Amazon/AmazonWarehouse.js - Updated with UK support
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  RefreshCw,
  Search,
  AlertTriangle,
  CheckCircle,
  ArrowLeft,
  Tag,
  ShoppingCart,
  Layers,
  List,
  Box,
  TrendingUp
} from 'lucide-react';
import './AmazonWarehouse.css';

const AmazonWarehouse = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('aggregated'); // 'aggregated' or 'detailed'
  const [stats, setStats] = useState({
    total_items: 0,
    total_skus: 0,
    total_available: 0,
    total_reserved: 0,
    total_inbound: 0,
    total_unfulfillable: 0,
    in_stock_items: 0,
    low_stock_items: 0,
    unique_asins: 0
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('ALL');
  const [stockFilter, setStockFilter] = useState('all');
  const [sortBy, setSortBy] = useState('asin');

  // Fetch Amazon inventory with view mode
  const fetchAmazonInventory = async (region = 'ALL', mode = 'aggregated') => {
    try {
      setLoading(true);
      setError('');
      console.log(`📦 Loading Amazon ${region} inventory (${mode} view)...`);

      const endpoint = mode === 'aggregated'
        ? `/api/amazon/inventory/${region}?view=aggregated`
        : `/api/amazon/inventory/${region}?view=detailed`;

      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Amazon ${region} inventory`);
      }

      const data = await response.json();
      console.log('Amazon inventory data:', data);

      if (data.success) {
        // Filter out Amazon.Found SKUs if in aggregated mode
        let processedInventory = data.inventory;
        if (mode === 'aggregated') {
          processedInventory = data.inventory.map(item => {
            // Clean up SKUs list to remove Amazon.Found entries
            if (item.SELLER_SKU && typeof item.SELLER_SKU === 'string') {
              const skus = item.SELLER_SKU.split(', ');
              const filteredSkus = skus.filter(sku => !sku.startsWith('Amazon.Found.'));
              item.SELLER_SKU = filteredSkus.join(', ');
              item.NUM_SKUS = filteredSkus.length;
            }
            return item;
          });
        } else {
          // In detailed view, filter out Amazon.Found rows entirely
          processedInventory = data.inventory.filter(item =>
            !item.SELLER_SKU?.startsWith('Amazon.Found.')
          );
        }

        setInventory(processedInventory);
        setStats(data.stats);
        console.log(`✅ Loaded ${processedInventory.length} items (${mode} view)`);
      } else {
        throw new Error(data.error || 'Failed to load Amazon inventory');
      }

    } catch (error) {
      console.error('❌ Error fetching Amazon inventory:', error);
      setError('Failed to load Amazon inventory: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAmazonInventory(regionFilter, viewMode);
  }, [regionFilter, viewMode]);

  // Filter and sort inventory
  const getFilteredAndSortedInventory = () => {
    let filtered = [...inventory];

    // Search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.SELLER_SKU?.toLowerCase().includes(lowerSearch) ||
        item.ASIN?.toLowerCase().includes(lowerSearch) ||
        item.PRODUCT_NAME?.toLowerCase().includes(lowerSearch)
      );
    }

    // Stock filter
    if (stockFilter === 'in_stock') {
      filtered = filtered.filter(item => item.TOTAL_AVAILABLE > 0);
    } else if (stockFilter === 'low_stock') {
      filtered = filtered.filter(item => item.TOTAL_AVAILABLE > 0 && item.TOTAL_AVAILABLE < 10);
    } else if (stockFilter === 'out_of_stock') {
      filtered = filtered.filter(item => item.TOTAL_AVAILABLE === 0 && item.TOTAL_STOCK === 0);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'asin':
          return (a.ASIN || '').localeCompare(b.ASIN || '');
        case 'sku':
          return (a.SELLER_SKU || '').localeCompare(b.SELLER_SKU || '');
        case 'total_stock':
          return (b.TOTAL_STOCK || 0) - (a.TOTAL_STOCK || 0);
        case 'available':
          return (b.TOTAL_AVAILABLE || 0) - (a.TOTAL_AVAILABLE || 0);
        case 'region':
          return (a.REGION || '').localeCompare(b.REGION || '');
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredInventory = getFilteredAndSortedInventory();

  // Stock status badge
  const getStockStatusBadge = (item) => {
    if (item.TOTAL_AVAILABLE === 0 && item.TOTAL_STOCK === 0) {
      return <span className="badge badge-danger">Out of Stock</span>;
    } else if (item.TOTAL_AVAILABLE > 0 && item.TOTAL_AVAILABLE < 10) {
      return <span className="badge badge-warning">Low Stock</span>;
    } else if (item.TOTAL_AVAILABLE > 0) {
      return <span className="badge badge-success">In Stock</span>;
    } else {
      return <span className="badge badge-secondary">Reserved Only</span>;
    }
  };

  // Get region badge - UPDATED WITH UK
  const getRegionBadge = (region) => {
    const badges = {
      'US': '🇺🇸 US',
      'UK': '🇬🇧 UK',
      'EU': '🇪🇺 EU',
      'CA': '🇨🇦 CA'
    };
    return <span className="region-badge">{badges[region] || region}</span>;
  };

  if (error) {
    return (
      <div className="amazon-container">
        <div className="error-container">
          <AlertTriangle size={48} className="error-icon" />
          <h2>Error Loading Amazon Inventory</h2>
          <p>{error}</p>
          <button onClick={() => fetchAmazonInventory(regionFilter, viewMode)} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="amazon-container">
      {/* Header */}
      <div className="amazon-header">
        <div className="header-left">
          <Link to="/dashboard" className="btn btn-back">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1>Amazon FBA Inventory</h1>
            <p className="subtitle">Manage your global marketplace inventory</p>
          </div>
        </div>
        <button onClick={() => fetchAmazonInventory(regionFilter, viewMode)} className="btn btn-primary" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Items</div>
            <div className="stat-value">{stats.total_items?.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <Tag size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total SKUs</div>
            <div className="stat-value">{stats.total_skus?.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon success">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Available</div>
            <div className="stat-value success">{stats.total_available?.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon warning">
            <ShoppingCart size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Reserved</div>
            <div className="stat-value warning">{stats.total_reserved?.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon info">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Inbound</div>
            <div className="stat-value info">{stats.total_inbound?.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon danger">
            <Box size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Low Stock Items</div>
            <div className="stat-value danger">{stats.low_stock_items?.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-container">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by SKU, ASIN, or Product Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="btn-group">
          <button
            className={`btn ${viewMode === 'aggregated' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('aggregated')}
          >
            <Layers size={16} />
            Aggregated
          </button>
          <button
            className={`btn ${viewMode === 'detailed' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('detailed')}
          >
            <List size={16} />
            Detailed
          </button>
        </div>

        {/* UPDATED REGION FILTER WITH UK */}
        <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} className="filter-select">
          <option value="ALL">🌍 All Regions</option>
          <option value="US">🇺🇸 United States</option>
          <option value="UK">🇬🇧 United Kingdom</option>
          <option value="EU">🇪🇺 Europe (excl. UK)</option>
          <option value="CA">🇨🇦 Canada</option>
        </select>

        <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className="filter-select">
          <option value="all">All Items</option>
          <option value="in_stock">In Stock</option>
          <option value="low_stock">Low Stock</option>
          <option value="out_of_stock">Out of Stock</option>
        </select>

        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
          <option value="asin">Sort by ASIN</option>
          <option value="sku">Sort by SKU</option>
          <option value="total_stock">Sort by Total Stock</option>
          <option value="available">Sort by Available</option>
          <option value="region">Sort by Region</option>
        </select>
      </div>

      {/* Inventory Table */}
      <div className="inventory-table-container">
        <div className="table-header">
          <h2>
            <Package size={20} />
            Inventory ({filteredInventory.length} {viewMode === 'aggregated' ? 'Products' : 'SKUs'})
            {regionFilter !== 'ALL' && ` - ${regionFilter}`}
          </h2>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading inventory...</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>ASIN</th>
                  <th>{viewMode === 'aggregated' ? 'SKUs' : 'SKU'}</th>
                  <th>Region</th>
                  <th>Available</th>
                  <th>Reserved</th>
                  <th>Total Stock</th>
                  <th>Inbound</th>
                  <th>Unfulfillable</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.map((item, index) => (
                  <tr key={`${item.ASIN}-${item.REGION}-${index}`}>
                    <td>
                      <div className="asin-cell">
                        <span className="asin">{item.ASIN}</span>
                        <span className="product-name">{item.PRODUCT_NAME?.substring(0, 40)}...</span>
                      </div>
                    </td>
                    <td>
                      {viewMode === 'aggregated' && item.NUM_SKUS > 1 ? (
                        <div className="sku-cell">
                          <span className="sku-count">{item.NUM_SKUS} SKUs</span>
                          <span className="sku-list">{item.SELLER_SKU}</span>
                        </div>
                      ) : (
                        <span className="sku">{item.SELLER_SKU}</span>
                      )}
                    </td>
                    <td>{getRegionBadge(item.REGION)}</td>
                    <td className="number">{item.TOTAL_AVAILABLE?.toLocaleString()}</td>
                    <td className="number">{item.TOTAL_RESERVED?.toLocaleString()}</td>
                    <td className="number bold">{item.TOTAL_STOCK?.toLocaleString()}</td>
                    <td className="number">{item.TOTAL_INBOUND?.toLocaleString()}</td>
                    <td className="number">
                      {item.TOTAL_UNFULFILLABLE > 0 && (
                        <span className="badge badge-danger">{item.TOTAL_UNFULFILLABLE}</span>
                      )}
                    </td>
                    <td>{getStockStatusBadge(item)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredInventory.length === 0 && (
              <div className="empty-state">
                <Package size={48} />
                <p>No inventory items found</p>
                {searchTerm && <p className="text-muted">Try adjusting your search terms</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with region info */}
      {!loading && (
        <div className="table-footer">
          <p className="text-muted">
            Showing {filteredInventory.length} items
            {regionFilter === 'ALL'
              ? ' from all regions (US, UK, EU, CA)'
              : ` from ${regionFilter}${regionFilter === 'EU' ? ' (excluding UK)' : ''}`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default AmazonWarehouse;